import { useState } from 'react';

import { PlusIcon } from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { toast } from 'sonner';

import { Button } from '@components/ui/button';
import { queryKeys } from '@constants/queryKeys.constant';
import { getErrorMessage } from '@helpers/getErrorMessage.helper';
import type { Agent } from '@models/agent.model';
import { agentsListQueryOptions } from '@services/agents/queryOptions';
import { agentsService } from '@services/agents/service';

import { AgentsTable } from './@components/AgentsTable';
import { DeleteAgentDialog } from './@components/DeleteAgentDialog';

export const Route = createFileRoute('/_dashboard/agents/')({
  component: AgentsPage,
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(agentsListQueryOptions()),
});

function AgentsPage() {
  const queryClient = useQueryClient();
  const [agentPendingDeletion, setAgentPendingDeletion] =
    useState<Agent | null>(null);

  const { data: agents = [], isLoading } = useQuery(agentsListQueryOptions());

  const { isPending: isDeleting, mutate: deleteAgent } = useMutation({
    mutationFn: (id: string) => agentsService.remove(id),
    onError: error => {
      toast.error('Warning', { description: getErrorMessage(error) });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.agents.all });
      toast.success('Agent deleted');
      setAgentPendingDeletion(null);
    },
  });

  return (
    <section
      className="grid min-h-full content-start gap-8"
      data-slot="agents-page"
    >
      <header className="flex min-w-0 items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-primary-dark text-3xl leading-tight font-extrabold md:text-4xl">
            Agents
          </h1>
          <p className="text-muted mt-3 max-w-2xl text-sm leading-6 md:text-base">
            Voice agents you&apos;ve configured. Test one or manage its
            settings.
          </p>
        </div>

        <Button asChild variant="primary">
          <Link to="/agents/new">
            <PlusIcon aria-hidden="true" className="size-4" />
            New agent
          </Link>
        </Button>
      </header>

      <AgentsTable
        agents={agents}
        isLoading={isLoading}
        onDeleteRequest={setAgentPendingDeletion}
      />

      <DeleteAgentDialog
        agent={agentPendingDeletion}
        isDeleting={isDeleting}
        onCancel={() => setAgentPendingDeletion(null)}
        onConfirm={agent => deleteAgent(agent.id)}
      />
    </section>
  );
}
